import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNativeGeolocation } from "./useNativeGeolocation";

interface LocationSyncOptions {
  enabled?: boolean;
  syncIntervalMinutes?: number;
  minDistanceMeters?: number;
  maxConsecutiveFailures?: number;
  circuitBreakerResetMinutes?: number;
}

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

interface CircuitBreakerStats {
  state: CircuitState;
  failureCount: number;
  lastFailureTime: number | null;
  nextRetryTime: number | null;
}

interface SyncAttempt {
  timestamp: number;
  success: boolean;
  latency: number;
  error?: string;
}

interface SyncMetrics {
  totalAttempts: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageLatency: number;
  lastSyncTime: number | null;
  recentAttempts: SyncAttempt[];
}

export const useLocationSync = (options: LocationSyncOptions = {}) => {
  const { 
    enabled = true, 
    syncIntervalMinutes = 10,
    minDistanceMeters = 500,
    maxConsecutiveFailures = 5,
    circuitBreakerResetMinutes = 5
  } = options;
  
  const { position } = useNativeGeolocation();
  const lastSyncedLocation = useRef<{ latitude: number; longitude: number } | null>(null);
  const [lastSyncedLocationState, setLastSyncedLocationState] = useState<{ latitude: number; longitude: number } | null>(null);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const positionRef = useRef(position);
  
  // Circuit Breaker state
  const [circuitBreakerStats, setCircuitBreakerStats] = useState<CircuitBreakerStats>({
    state: 'CLOSED',
    failureCount: 0,
    lastFailureTime: null,
    nextRetryTime: null
  });
  const consecutiveFailures = useRef<number>(0);
  const circuitOpenTime = useRef<number | null>(null);
  
  // Sync Metrics
  const [syncMetrics, setSyncMetrics] = useState<SyncMetrics>({
    totalAttempts: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    averageLatency: 0,
    lastSyncTime: null,
    recentAttempts: []
  });

  // ✅ ATUALIZAR positionRef sempre que position mudar
  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  // Calculate distance between two points in meters
  const calculateDistance = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const openCircuit = () => {
    const failureCount = consecutiveFailures.current; // Save before reset
    consecutiveFailures.current = 0;
    circuitOpenTime.current = Date.now();
    const nextRetry = Date.now() + (circuitBreakerResetMinutes * 60 * 1000);
    
    setCircuitBreakerStats({
      state: 'OPEN',
      failureCount: failureCount,
      lastFailureTime: Date.now(),
      nextRetryTime: nextRetry
    });
    
    console.warn("Circuit breaker OPEN - sync paused", {
      failureCount: failureCount,
      nextRetryTime: new Date(nextRetry).toISOString()
    });
  };

  const closeCircuit = () => {
    consecutiveFailures.current = 0;
    circuitOpenTime.current = null;
    
    setCircuitBreakerStats({
      state: 'CLOSED',
      failureCount: 0,
      lastFailureTime: null,
      nextRetryTime: null
    });
    
    console.log("Circuit breaker CLOSED - sync resumed");
  };

  const tryHalfOpen = () => {
    setCircuitBreakerStats(prev => ({
      ...prev,
      state: 'HALF_OPEN'
    }));
    
    console.log("Circuit breaker HALF_OPEN - attempting recovery");
  };

  const syncLocation = async (lat: number, lng: number) => {
    const startTime = Date.now();
    
    // Check circuit breaker state
    if (circuitBreakerStats.state === 'OPEN') {
      const now = Date.now();
      const shouldRetry = circuitOpenTime.current && 
        (now - circuitOpenTime.current) >= (circuitBreakerResetMinutes * 60 * 1000);
      
      if (shouldRetry) {
        tryHalfOpen();
      } else {
        console.log("Circuit breaker OPEN - skipping sync", {
          nextRetryIn: circuitBreakerStats.nextRetryTime 
            ? Math.round((circuitBreakerStats.nextRetryTime - now) / 1000) + 's'
            : 'calculating...'
        });
        return;
      }
    }

    try {
      if (lastSyncedLocation.current) {
        const distance = calculateDistance(
          lastSyncedLocation.current.latitude,
          lastSyncedLocation.current.longitude,
          lat,
          lng
        );

        if (distance < minDistanceMeters) {
          console.log("Location unchanged, skipping sync", { distance });
          return;
        }
      }

      console.log("Syncing location to backend", { lat, lng });

      const { data, error } = await supabase.functions.invoke("update-location", {
        body: { latitude: lat, longitude: lng },
      });

      const latency = Date.now() - startTime;

      if (error) {
        console.error("Failed to sync location", error);
        
        // Record failed attempt
        setSyncMetrics(prev => {
          const newAttempt: SyncAttempt = {
            timestamp: Date.now(),
            success: false,
            latency,
            error: error.message
          };
          const recentAttempts = [...prev.recentAttempts, newAttempt].slice(-50);
          
          return {
            totalAttempts: prev.totalAttempts + 1,
            successfulSyncs: prev.successfulSyncs,
            failedSyncs: prev.failedSyncs + 1,
            averageLatency: prev.averageLatency,
            lastSyncTime: Date.now(),
            recentAttempts
          };
        });
        
        consecutiveFailures.current++;
        
        setCircuitBreakerStats(prev => ({
          ...prev,
          failureCount: consecutiveFailures.current,
          lastFailureTime: Date.now()
        }));
        
        if (consecutiveFailures.current >= maxConsecutiveFailures) {
          openCircuit();
        }
        
        return;
      }

      console.log("Location synced successfully", data);
      lastSyncedLocation.current = { latitude: lat, longitude: lng };
      setLastSyncedLocationState({ latitude: lat, longitude: lng });
      
      // Record successful attempt
      setSyncMetrics(prev => {
        const newAttempt: SyncAttempt = {
          timestamp: Date.now(),
          success: true,
          latency
        };
        const recentAttempts = [...prev.recentAttempts, newAttempt].slice(-50);
        const totalSuccessful = prev.successfulSyncs + 1;
        const avgLatency = ((prev.averageLatency * prev.successfulSyncs) + latency) / totalSuccessful;
        
        return {
          totalAttempts: prev.totalAttempts + 1,
          successfulSyncs: totalSuccessful,
          failedSyncs: prev.failedSyncs,
          averageLatency: avgLatency,
          lastSyncTime: Date.now(),
          recentAttempts
        };
      });
      
      if (consecutiveFailures.current > 0 || circuitBreakerStats.state !== 'CLOSED') {
        closeCircuit();
      }
      
    } catch (error) {
      console.error("Error syncing location", error);
      
      const latency = Date.now() - startTime;
      
      // Record failed attempt
      setSyncMetrics(prev => {
        const newAttempt: SyncAttempt = {
          timestamp: Date.now(),
          success: false,
          latency,
          error: error instanceof Error ? error.message : 'Unknown error'
        };
        const recentAttempts = [...prev.recentAttempts, newAttempt].slice(-50);
        
        return {
          totalAttempts: prev.totalAttempts + 1,
          successfulSyncs: prev.successfulSyncs,
          failedSyncs: prev.failedSyncs + 1,
          averageLatency: prev.averageLatency,
          lastSyncTime: Date.now(),
          recentAttempts
        };
      });
      
      consecutiveFailures.current++;
      
      setCircuitBreakerStats(prev => ({
        ...prev,
        failureCount: consecutiveFailures.current,
        lastFailureTime: Date.now()
      }));
      
      if (consecutiveFailures.current >= maxConsecutiveFailures) {
        openCircuit();
      }
    }
  };

  useEffect(() => {
    // ✅ Limpar intervalo anterior sempre que dependências mudarem
    if (syncTimeoutRef.current) {
      clearInterval(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    // ✅ Se desabilitado ou sem posição, não cria intervalo
    if (!enabled || !position) {
      return;
    }

    const lat = position.coords.latitude;
    const lng = position.coords.longitude;

    // Sync inicial imediato
    syncLocation(lat, lng);

    // ✅ Criar único intervalo lendo posição do ref (não depende de position)
    const intervalMs = Math.max(syncIntervalMinutes * 60 * 1000, 1);
    syncTimeoutRef.current = setInterval(() => {
      const currentPosition = positionRef.current;
      if (currentPosition) {
        syncLocation(currentPosition.coords.latitude, currentPosition.coords.longitude);
      }
    }, intervalMs);

    return () => {
      if (syncTimeoutRef.current) {
        clearInterval(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [enabled, syncIntervalMinutes, minDistanceMeters]);

  return {
    lastSyncedLocation: lastSyncedLocationState,
    syncLocation,
    circuitBreakerStats,
    syncMetrics,
  };
};
